import { useState } from 'react';
import uuid from 'react-uuid';
import classNames from 'classnames';
import Icon from '../Icon';

type Props = {
  as?: 'input' | 'textarea';
  status?: 'error' | 'success';
  errorMsg?: string;
  name?: string;
  label: string;
  defaultId?: string;
  defaultValue?: string;
  autoComplete?: string;
  className?: string;
  readonly?: boolean;
};

function InputText({
  as: Tag = 'input',
  status,
  errorMsg,
  name,
  label,
  defaultId,
  defaultValue,
  autoComplete,
  className,
  readonly
}: Props) {
  const [value, setValue] = useState<string>(
    defaultValue || ''
  );

  const [isFocused, setIsFocused] = useState(false);

  const [id] = useState<string>(defaultId || uuid());

  const handleChange = (
    e: React.BaseSyntheticEvent
  ): void => {
    setValue(e.target.value);
  };

  return (
    <div
      className={classNames([
        'relative bg-white',
        className
      ])}
    >
      <Tag
        className={classNames([
          'block w-full rounded-xl border-2 pt-4 pb-1.5 pl-4 pr-12 font-circular text-sm font-medium !leading-tight text-sl-blue !outline-none transition-colors duration-300 placeholder:opacity-0 focus:ring-0 md:pt-3.5 md:text-lg',
          !status &&
            'border-sl-blue border-opacity-25 focus:border-opacity-100',
          status === 'success' &&
            'border-sl-green border-opacity-100',
          status === 'error' &&
            'border-sl-red border-opacity-100 text-sl-red text-opacity-100',
          Tag === 'input' && 'h-[48px] md:h-[52px]',
          Tag === 'textarea' &&
            'h-[120px] resize-none !pt-4 !leading-snug',
          readonly &&
            '!pt-1.5 !pr-4 focus:border-opacity-25'
        ])}
        id={id}
        type={Tag === 'input' ? 'text' : undefined}
        value={value}
        placeholder={label}
        onChange={handleChange}
        name={name}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        autoComplete={autoComplete}
        readOnly={readonly}
      />
      <label
        htmlFor={id}
        className={classNames([
          'absolute left-4 cursor-text select-none pl-[2px] font-circular font-medium !leading-none text-sl-blue transition-all duration-300',
          status === 'success' && 'text-sl-green',
          status === 'error' && 'text-sl-red',
          value || isFocused
            ? 'top-2 text-[9px] text-opacity-100 md:text-[10px]'
            : '',
          !value &&
            !isFocused &&
            'top-[17px] text-sm text-opacity-50 md:top-4 md:text-lg',
          readonly && 'hidden'
        ])}
      >
        {label}
      </label>
      <span
        className={classNames([
          'peer absolute right-2 top-1/2 -translate-y-1/2 p-2 text-lg !leading-none transition-opacity duration-300',
          status === 'error' && 'text-sl-red',
          status === 'success' && 'text-sl-green'
        ])}
      >
        <Icon
          icon="checkmark"
          className={`${
            status === 'success' ? 'block' : 'hidden'
          }`}
        />
        <Icon
          icon="circle-info"
          className={`${
            status === 'error' ? 'block' : 'hidden'
          }`}
        />
      </span>
      {errorMsg && status === 'error' && (
        <span className="invisible absolute bottom-full right-0 -mb-2 max-w-full rounded-lg border border-sl-red border-opacity-20 bg-[#f9e8e8] px-2 py-1 text-left font-sans text-xs font-normal text-sl-red peer-hover:visible">
          <span>{errorMsg}</span>
          <span className="absolute right-5 -bottom-1 h-2 w-2 rotate-[135deg] border-t  border-r border-sl-red border-opacity-20 bg-[#f9e8e8]" />
        </span>
      )}
    </div>
  );
}

export default InputText;
